import React from 'react';
import { TFunction } from 'i18next';
import { MRT_ColumnDef } from 'material-react-table';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { Tooltip } from '@mui/material';
import {
  renderFileReceivedTimeStampCell,
  createInsuredLenderNameRenderer,
  renderDefaultCell,
  renderLoanStatusCell,
  renderCmhcAccountNumberCell,
  borrowerNameRenderer,
  renderDateCell,
  renderRemark,
  createStatusRenderer
} from './vriCellRenderer';
import { LoanSearchPoolResponse } from './types';
import styles from './styles.module.scss';
import { renderDataTestId } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementTable/vriTableColumns';

export const createPortfolioColumns = (
  t: TFunction,
  language: string
): MRT_ColumnDef<LoanSearchPoolResponse>[] => {
  const insuredLenderNameRenderer = createInsuredLenderNameRenderer(language);
  const LoanStatusCellRenderer = renderLoanStatusCell(t);
  const cmhcAccountNumberCellRenderer = renderCmhcAccountNumberCell(t);
  const renderStatus = createStatusRenderer(t);

  return [
    {
      accessorKey: 'fileReceivedTimestamp' as const,
      header: t('Request-Receipt-Date'),
      Cell: renderFileReceivedTimeStampCell,
      enableSorting: true,
      size: language === 'en' ? 240 : 320,
      muiTableHeadCellProps: renderDataTestId('fileReceivedTimestamp')
    },
    {
      accessorKey: 'cmhcAccountNumber' as const,
      header: t('cmhc-account-number'),
      muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
        return {
          'aria-label':
            language === 'en'
              ? `CMHC Account Number ${props.cell.getValue()}`
              : `Caractéristiques faible CMHC ${props.cell.getValue()}`
        };
      },
      Cell: cmhcAccountNumberCellRenderer,
      enableSorting: true,
      size: 300,
      muiTableHeadCellProps: renderDataTestId('cmhcAccountNumber')
    },
    {
      accessorKey: 'loanStatusCode' as const,
      header: t('Loan-Status'),
      Cell: LoanStatusCellRenderer,
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('loanStatusCode')
    },
    {
      accessorKey: 'lowRatioCmhcAccountNumber' as const,
      header: t('cmhc-low-ratio-account-number'),
      muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
        return {
          'aria-label':
            language === 'en'
              ? `CMHC Low Ratio Account Number ${props.cell.getValue()}`
              : `Caractéristiques du ratio faible CMHC ${props.cell.getValue()}`
        };
      },
      Cell: cmhcAccountNumberCellRenderer,
      enableSorting: true,
      size: 335,
      muiTableHeadCellProps: renderDataTestId('lowRatioCmhcAccountNumber')
    },
    {
      accessorKey: 'lenderLoanNumber' as const,
      header: t('lender-reference-number'),
      Cell: renderDefaultCell,
      enableSorting: true,
      size: 320,
      muiTableHeadCellProps: renderDataTestId('lenderLoanNumber')
    },
    {
      accessorKey: 'propertyAddress' as const,
      header: t('property-address'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Property-Address-Tool-Tip-Text')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      Cell: renderDefaultCell,
      enableSorting: true,
      size: language === 'en' ? 235 : 275,
      muiTableHeadCellProps: renderDataTestId('propertyAddress')
    },
    {
      accessorKey: 'borrower' as const,
      header: t('Borrower-Name'),
      Cell: borrowerNameRenderer(),
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('borrower')
    },
    {
      accessorKey: 'borrowerDateOfBirth' as const,
      header: t('Borrower-Date-of-Birth'),
      Cell: renderDateCell,
      enableSorting: true,
      size: 275,
      muiTableHeadCellProps: renderDataTestId('borrowerDateOfBirth')
    },
    {
      accessorKey: 'coBorrower' as const,
      header: t('Co-borrower-Name'),
      Cell: borrowerNameRenderer(),
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('coBorrower')
    },
    {
      accessorKey: 'coBorrowerDateOfBirth' as const,
      header: t('Co-borrower-Date-of-Birth'),
      Cell: renderDateCell,
      enableSorting: true,
      size: 275,
      muiTableHeadCellProps: renderDataTestId('coBorrowerDateOfBirth')
    },
    {
      accessorKey: 'masterPoolCmhcAccountNumber' as const,
      header: t('master-pool-number'),
      muiTableBodyCellProps(props: { cell: { getValue: () => any } }) {
        return {
          'aria-label':
            language === 'en'
              ? `Master Pool CMHC Account Number ${props.cell.getValue()}`
              : `Caractéristiques faible CMHC ${props.cell.getValue()}`
        };
      },
      Cell: cmhcAccountNumberCellRenderer,
      enableSorting: true,
      size: 300,
      muiTableHeadCellProps: renderDataTestId('masterPoolCmhcAccountNumber')
    },
    {
      accessorKey: 'masterPoolStatus' as const,
      header: t('Master-Pool-Status'),
      Cell: renderStatus,
      enableSorting: true,
      size: language === 'en' ? 240 : 320,
      muiTableHeadCellProps: renderDataTestId('masterPoolStatus')
    },
    {
      accessorKey: 'approvalDate' as const,
      header: t('Approval-Date'),
      Cell: renderDateCell,
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('approvalDate')
    },
    {
      accessorKey: 'cancellationDate' as const,
      header: t('Cancellation-Date'),
      Cell: renderFileReceivedTimeStampCell,
      enableSorting: true,
      size: 240,
      muiTableHeadCellProps: renderDataTestId('cancellationDate')
    },
    {
      accessorKey: 'insuredLenderName' as const,
      header: t('Insured-Approved-Lender-Name'),
      Cell: insuredLenderNameRenderer,
      enableSorting: true,
      size: 320,
      muiTableHeadCellProps: renderDataTestId('insuredLenderName')
    },
    {
      accessorKey: 'underwritingLenderName' as const,
      header: t('Underwriting-Approved-Lender-Name'),
      Cell: insuredLenderNameRenderer,
      enableSorting: true,
      size: 320,
      muiTableHeadCellProps: renderDataTestId('underwritingLenderName')
    },
    {
      accessorKey: 'latestRemark' as const,
      header: t('Remarks'),
      Header: ({ column }: any) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <div>{column.columnDef?.header}</div>
            <Tooltip
              title={t('Remarks-Tooltip')}
              placement="top-start"
              arrow
              slotProps={{
                tooltip: {
                  className: styles.loansInventoryToolTip
                },
                arrow: {
                  className: styles.tooltipArrow
                }
              }}
            >
              <div
                style={{
                  display: 'inline-block',
                  marginLeft: '8px'
                }}
              >
                <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
              </div>
            </Tooltip>
          </div>
        );
      },
      muiTableBodyCellProps(props) {
        return {
          'aria-label': language === 'en' ? `remarks ${props.row.id}` : `remarques ${props.row.id}`
        };
      },
      Cell: renderRemark,
      enableSorting: false,
      size: 200,
      muiTableHeadCellProps: renderDataTestId('latestRemark')
    }
  ];
};
